import React, { createContext, useContext, useState, ReactNode } from 'react';

type Position = { x: number; y: number };

interface WidgetContextType {
    position: Position;
    updatePosition: (pos: Position) => void;
}

const WidgetContext = createContext<WidgetContextType | undefined>(undefined);

export const WidgetProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    
    // Initialize state with a function (lazy initialization)
    const [position, setPosition] = useState<Position>(() => {
        if (typeof window === 'undefined') return { x: 0, y: 0 };

        // 1. Check if user already moved it (Saved in LocalStorage)
        const saved = localStorage.getItem('ark_widget_pos');
        if (saved) {
            try {
                return JSON.parse(saved);
            } catch (e) { /* ignore error */ }
        }

        // 2. If no save, set default to BOTTOM RIGHT
        // "Not too bottom": We subtract larger numbers from the window dimensions
        return {
            x: window.innerWidth - 90,  // ~90px from the right edge
            y: window.innerHeight - 150 // ~150px from the bottom (Floating up a bit)
        };
    });

    const updatePosition = (pos: Position) => {
        setPosition(pos);
        localStorage.setItem('ark_widget_pos', JSON.stringify(pos));
    };

    return (
        <WidgetContext.Provider value={{ position, updatePosition }}>
            {children}
        </WidgetContext.Provider>
    );
};

export const useWidgetContext = () => {
    const context = useContext(WidgetContext);
    if (!context) {
        throw new Error('useWidgetContext must be used within a WidgetProvider');
    }
    return context;
};